// src/App.tsx - Updated for Batch Processing Support
// Handles BOTH OLD and NEW schemas from backend
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import PolicyTimingDialog from "./components/DataViewer/PolicyTimingDialog";
import { PolicyTimingEntry } from "./types/index";
import {
  Box,
  Typography,
  Alert,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Stack,
  useMediaQuery,
  useTheme,
  CircularProgress,
  TextField,
  AppBar,
  Toolbar,
  InputAdornment,
  Tooltip,
  Pagination,
  PaginationItem
} from "@mui/material";
import {
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  MenuBook as QuickRefIcon,
  AutoAwesome as MagicWandIcon,
  Assessment as DashboardIcon,
  Send as SendIcon,
  Layers as BatchIcon,
  ContentCopy as CopyIcon,
  InsertDriveFile as ReportIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  AccountCircle as AccountIcon,
  Search as SearchIcon,
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";
import CachedIcon from "@mui/icons-material/Cached";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Import custom hooks
import { useNotifications } from "./hooks";

// Import components
import { NotificationSnackbar } from "./components";
import UploadManager from "./components/Upload/UploadManager";
import JsonTreeViewer from "./components/DataViewer/JsonTreeViewer/JsonTreeViewer";
import LossRunDashboard from "./components/LossRunDashboard/LossRunDashboard";

// Import API service
import { apiService, PaginationMeta } from "./services/api";

// Dark theme configuration
const theme = createTheme({
  palette: {
    mode: "dark",
    primary: { main: "#0F172A" },
    secondary: { main: "#00d4ff" },
    background: {
      default: "#F8FAFC",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#0F172A",
      secondary: "#64748B",
    },
    error: { main: "#EF4444" },
    warning: { main: "#F59E0B" },
    success: { main: "#10B981" },
  },
  typography: {
    fontFamily: "sans-serif",
    h5: { fontWeight: 700 },
    h6: { fontWeight: 600 },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow:
            "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
          border: "1px solid #E2E8F0",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          borderRadius: 8,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 700,
          color: "#64748B",
          backgroundColor: "#F8FAFC",
          textTransform: "uppercase",
          fontSize: "0.75rem",
          letterSpacing: "0.05em",
        },
      },
    },
  },
});

// API Configuration
const API_ENDPOINT = "https://api.elevatenow.tech/query";

// Helper functions for batch analysis
function createBatchAnalysisSummary(record: any): any {
  if (!record.policies || record.policies.length === 0) return null;

  let totalClaimsExtracted = 0,
    totalMatched = 0,
    totalUnmatched = 0;
  const attentionRequired: any[] = [],
    noConcerns: any[] = [],
    policySummaries: any[] = [];

  record.policies.forEach((policy: any) => {
    const augPolicy = policy.augmented_policy;
    if (!augPolicy) return;

    const jsonData = augPolicy.jsonData || augPolicy;
    const lossRuns = jsonData.loss_runs || {};
    const derivedAttrs = lossRuns.derived_attributes || {};
    const summaryDerived = derivedAttrs.summary || {};
    const metadata = lossRuns._metadata || {};
    const appInfo = jsonData.Application || {};
    const generalInfo = jsonData.GeneralInfo || {};
    const byProduct = derivedAttrs.by_product || {};
    const claimsByYear = lossRuns.claims_by_year || {};

    const policyPremium = summaryDerived.total_premium || 0;
    const policyIncurred = summaryDerived.total_incurred || 0;
    const policyMatched = metadata.claims_matched || 0;
    const policyUnmatched = metadata.claims_unmatched || 0;
    const policyOpen = summaryDerived.open_claims || 0;
    const policyClosed = summaryDerived.closed_claims || 0;
    const lossRatio =
      policyPremium > 0 ? (policyIncurred / policyPremium) * 100 : 0;

    totalClaimsExtracted += policyMatched + policyUnmatched;
    totalMatched += policyMatched;
    totalUnmatched += policyUnmatched;

    const insuredName =
      generalInfo.entityFullName ||
      jsonData.SubmissionInfo?.txtInsuredName ||
      appInfo.InsuredFullName ||
      appInfo.hdEntityName ||
      "Unknown";

    const productBreakdown: any[] = [];
    Object.entries(byProduct).forEach(([productName, data]: [string, any]) => {
      if (data.claim_count > 0)
        productBreakdown.push({
          product: productName,
          claims: data.claim_count,
          incurred: data.total_incurred || 0,
          loss_ratio_pct: data.net_loss_ratio_pct || 0,
        });
    });
    productBreakdown.sort((a, b) => b.incurred - a.incurred);

    const allClaims: any[] = [];
    Object.entries(claimsByYear).forEach(([year, claims]: [string, any]) => {
      if (Array.isArray(claims))
        claims.forEach((claim: any) => allClaims.push({ ...claim, year }));
    });

    const largeLosses = allClaims
      .filter((c) => (c.total_incurred || 0) > 25000)
      .sort((a, b) => (b.total_incurred || 0) - (a.total_incurred || 0))
      .slice(0, 3)
      .map((c) => ({
        loss_number: c.loss_number,
        product: c.product,
        incurred: c.total_incurred || 0,
        description: (c.loss_description || "").substring(0, 50),
      }));

    const concerns: string[] = [];
    if (lossRatio > 60)
      concerns.push(`High loss ratio (${lossRatio.toFixed(1)}%)`);
    if (lossRatio > 100) concerns.push("Loss ratio exceeds premium");
    if (policyUnmatched > 0)
      concerns.push(`${policyUnmatched} unmatched claims`);
    if (policyOpen > 0) concerns.push(`${policyOpen} open claims`);
    if (largeLosses.length > 0)
      concerns.push(`${largeLosses.length} large loss(es) over $25K`);

    const positives: string[] = [];
    if (lossRatio < 30 && policyMatched > 0)
      positives.push("Favorable loss ratio (<30%)");
    if (policyUnmatched === 0 && policyMatched > 0)
      positives.push("All claims matched");
    if (policyClosed === policyMatched && policyMatched > 0)
      positives.push("All claims closed");

    const policySummary = {
      appnum: policy.appnum,
      insured: insuredName,
      policy_number: appInfo.policyNumber || "N/A",
      program: appInfo.Program || "Unknown",
      premium: policyPremium,
      total_incurred: policyIncurred,
      loss_ratio_pct: lossRatio.toFixed(1),
      claims_matched: policyMatched,
      claims_unmatched: policyUnmatched,
      open_claims: policyOpen,
      closed_claims: policyClosed,
      year_range: summaryDerived.year_range || "N/A",
      products: productBreakdown,
      large_losses: largeLosses,
      concerns,
      positives,
    };
    policySummaries.push(policySummary);

    if (concerns.length > 0)
      attentionRequired.push({
        appnum: policy.appnum,
        insured: insuredName,
        loss_ratio_pct: lossRatio.toFixed(1),
        incurred: policyIncurred,
        concerns,
        large_losses: largeLosses,
      });
    else
      noConcerns.push({
        appnum: policy.appnum,
        insured: insuredName,
        loss_ratio_pct: lossRatio.toFixed(1),
        incurred: policyIncurred,
        positives,
      });
  });

  const matchRate =
    totalClaimsExtracted > 0
      ? ((totalMatched / totalClaimsExtracted) * 100).toFixed(0)
      : "100";
  return {
    _workflow: "BATCH_ANALYSIS",
    processing_status: {
      policies_submitted: record.policies.length,
      policies_processed: policySummaries.length,
      total_claims_extracted: totalClaimsExtracted,
      claims_matched: totalMatched,
      claims_unmatched: totalUnmatched,
      match_rate_pct: matchRate,
    },
    triage: { attention_required: attentionRequired, no_concerns: noConcerns },
    policies: policySummaries,
  };
}

function createAnalysisSummary(augmentedPolicy: any): any {
  if (!augmentedPolicy) return null;
  try {
    const jsonData = augmentedPolicy.jsonData || augmentedPolicy;
    const appInfo = jsonData.Application || {};
    const generalInfo = jsonData.GeneralInfo || {};
    const submissionInfo = jsonData.SubmissionInfo || {};
    const lossRuns = jsonData.loss_runs || {};

    if (!lossRuns || Object.keys(lossRuns).length === 0)
      return {
        _workflow: "ANALYSIS",
        error: "No loss_runs data found",
        policy: {
          number: appInfo.policyNumber || "Unknown",
          insured: "Unknown",
        },
      };

    const metadata = lossRuns._metadata || {};
    const derivedAttrs = lossRuns.derived_attributes || {};
    const claimsByYear = lossRuns.claims_by_year || {};

    const allClaims: any[] = [];
    Object.entries(claimsByYear).forEach(([year, claims]: [string, any]) => {
      if (Array.isArray(claims))
        claims.forEach((claim: any) =>
          allClaims.push({ ...claim, _year: year }),
        );
    });

    const byProductDerived = derivedAttrs.by_product || {};
    const summaryDerived = derivedAttrs.summary || {};

    const productSummary = Object.entries(byProductDerived)
      .map(([product, data]: [string, any]) => ({
        product,
        claim_count: data.claim_count || 0,
        total_incurred: data.total_incurred || 0,
        loss_ratio_pct: data.net_loss_ratio_pct || 0,
        premium: data.premium || 0,
      }))
      .filter((p) => p.claim_count > 0 || p.premium > 0);

    const topClaims = allClaims
      .sort((a, b) => (b.total_incurred || 0) - (a.total_incurred || 0))
      .slice(0, 5)
      .map((claim) => ({
        loss_number: claim.loss_number,
        date_of_loss: claim.date_of_loss,
        product: claim.product,
        total_incurred: claim.total_incurred,
        status: claim.status,
        description: (claim.loss_description || "").substring(0, 100),
      }));

    const insuredName =
      generalInfo.entityFullName ||
      submissionInfo.txtInsuredName ||
      appInfo.InsuredFullName ||
      appInfo.hdEntityName ||
      "Unknown";

    return {
      _workflow: "ANALYSIS",
      policy: {
        number: appInfo.policyNumber || "Unknown",
        insured: insuredName,
        program: appInfo.Program || "Unknown",
      },
      processing_summary: {
        claims_processed: metadata.claims_processed || allClaims.length,
        claims_matched: metadata.claims_matched || allClaims.length,
        claims_unmatched: metadata.claims_unmatched || 0,
      },
      financial_summary: {
        total_premium: summaryDerived.total_premium || 0,
        total_incurred: summaryDerived.total_incurred || 0,
        loss_ratios: {
          pure_pct: summaryDerived.blended_pure_loss_ratio_pct || 0,
          net_pct: summaryDerived.blended_net_loss_ratio_pct || 0,
        },
      },
      claim_counts: {
        total: summaryDerived.total_claims || allClaims.length,
        open: summaryDerived.open_claims || 0,
        closed: summaryDerived.closed_claims || 0,
      },
      by_product: productSummary,
      top_claims: topClaims,
    };
  } catch (e) {
    return null;
  }
}

// Interfaces
interface UploadFile {
  id: string;
  file: File;
  status: "pending" | "ready" | "error";
  error?: string;
}
interface BatchPolicy {
  appnum: string;
  augmented_policy: any;
  message: string;
  status?: string;
  suggested_filename?: string;
  stats?: {
    status?: string;
    sub_workflow_id?: string;
    suggested_filename?: string;
  };
}
interface SessionRecord {
  id: string;
  pdfFileName: string;
  jsonFileName: string;
  status: "processing" | "completed" | "failed";
  submittedAt: string;
  completedAt?: string;
  duration?: number;
  error?: string;
  augmentedPolicy?: any;
  policies?: BatchPolicy[];
  isBatch?: boolean;
  policyCount?: number;
  workflow_id?: string;
  batch_id?: string;
  Policy_Timing?: PolicyTimingEntry[];
}

function detectSchemaVersion(record: any): "NEW" | "OLD" {
  return Array.isArray(record.LossRunMapper_Processed) ? "NEW" : "OLD";
}

function extractPolicies(record: any): BatchPolicy[] {
  const schemaVersion = detectSchemaVersion(record);
  if (schemaVersion === "NEW") {
    const lrpArray = record.LossRunMapper_Processed || [];
    return lrpArray.map((policy: any) => ({
      appnum: policy.appnum || "unknown",
      augmented_policy: policy.augmented_policy || {},
      message: policy.message || "",
      status: policy.stats?.status || policy.status || "unknown",
      suggested_filename:
        policy.stats?.suggested_filename || policy.suggested_filename || "",
      stats: policy.stats || {},
    }));
  } else {
    const lrp = record.LossRunMapper_Processed || {};
    return [
      {
        appnum: "",
        augmented_policy: lrp.augmented_policy || {},
        message: lrp.message || "",
        status: lrp.stats?.status || "unknown",
        suggested_filename: lrp.stats?.suggested_filename || "",
        stats: lrp.stats || {},
      },
    ];
  }
}

function extractFiles(record: any): { pdfs: any[]; jsons: any[] } {
  const schemaVersion = detectSchemaVersion(record);
  if (schemaVersion === "NEW")
    return {
      pdfs: record.files_uploaded?.loss_run_files || [],
      jsons: record.files_uploaded?.policy_json || [],
    };
  const files = record.files_uploaded || [];
  if (Array.isArray(files) && files.length > 0 && typeof files[0] === "object")
    return {
      pdfs: files.filter((f: any) => f.file_type === "application/pdf"),
      jsons: files.filter((f: any) => f.file_type === "application/json"),
    };
  return {
    pdfs: [{ filename: files[0] || "loss_run.pdf" }],
    jsons: [{ filename: files[1] || "policy.json" }],
  };
}

function transformToSessionRecord(item: any): SessionRecord {
  const policies = extractPolicies(item);
  const files = extractFiles(item);
  const isBatch = policies.length > 1;
  let pdfFileName = isBatch
    ? `${files.pdfs.length} PDFs (Batch)`
    : files.pdfs[0]?.filename || "loss_run.pdf";
  const jsonFileName = files.jsons[0]?.filename || "policy.json";

  return {
    id:
      item.workflow_id ||
      item._id?.$oid ||
      (typeof item._id === "string" ? item._id : null) ||
      `history_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    pdfFileName,
    jsonFileName,
    status: item.status || (item.error ? "failed" : "completed"),
    submittedAt: item.timestamp || new Date().toISOString(),
    completedAt: item.completed_at || item.timestamp,
    duration: (item.duration || item.total_runtime || 0) * 1000,
    augmentedPolicy: policies[0]?.augmented_policy || {},
    policies,
    isBatch,
    policyCount: policies.length,
    workflow_id: item.workflow_id,
    batch_id: item.batch_id,
    error: item.error,
    Policy_Timing: item.Policy_Timing || [],
  };
}

const App: React.FC = () => {
  // Auth0 hooks
  const {
    isAuthenticated,
    isLoading: authLoading,
    loginWithRedirect,
    logout,
    user,
    getAccessTokenSilently,
  } = useAuth0();

  const [timingOpen, setTimingOpen] = useState(false);
  const [timingRecord, setTimingRecord] = useState<SessionRecord | null>(null);
  const [uploadFiles, setUploadFiles] = useState<UploadFile[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [sessionRecords, setSessionRecords] = useState<SessionRecord[]>([]);
  const [dbRecords, setDbRecords] = useState<SessionRecord[]>([]);
  const [historyTab, setHistoryTab] = useState(0);
  const isInitialMount = useRef(true);
  const isRedirectingToLogin = useRef(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<SessionRecord | null>(
    null,
  );
  const [activeTab, setActiveTab] = useState(0);
  const [selectedPolicyIndex, setSelectedPolicyIndex] = useState(0);
  const [allPoliciesViewMode, setAllPoliciesViewMode] = useState<
    "tree" | "raw"
  >("tree");
  const [copySuccess, setCopySuccess] = useState(false);
  const [analysisDialogOpen, setAnalysisDialogOpen] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisThreadId, setAnalysisThreadId] = useState<number | null>(null);
  const [analysisSelectedRecord, setAnalysisSelectedRecord] =
    useState<SessionRecord | null>(null);
  const [followUpQuestion, setFollowUpQuestion] = useState<string>("");
  const [conversationHistory, setConversationHistory] = useState<
    Array<{ role: string; content: string }>
  >([]);
  const [isLoadingView, setIsLoadingView] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Dashboard state
  const [dashboardOpen, setDashboardOpen] = useState(false);
  const [dashboardRecord, setDashboardRecord] = useState<SessionRecord | null>(
    null,
  );
  const [dashboardPolicyIndex, setDashboardPolicyIndex] = useState(0);

  const [isLoadingDashboard, setIsLoadingDashboard] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState<PaginationMeta | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const filteredRecords = useMemo(() => {
    const allRecords = historyTab === 0 ? sessionRecords : dbRecords;
    if (!searchTerm.trim()) return allRecords;

    const term = searchTerm.toLowerCase();
    return allRecords.filter((record) => {
      const batchId = record.batch_id?.toLowerCase() || "";
      const pdfName = record.pdfFileName?.toLowerCase() || "";
      const jsonName = record.jsonFileName?.toLowerCase() || "";
      const status = record.status?.toLowerCase() || "";

      return (
        batchId.includes(term) ||
        pdfName.includes(term) ||
        jsonName.includes(term) ||
        status.includes(term)
      );
    });
  }, [sessionRecords, dbRecords, searchTerm, historyTab]);

  // Reconciliation Report state
  const [reconciliationDialogOpen, setReconciliationDialogOpen] =
    useState(false);
  const [reconciliationReports, setReconciliationReports] = useState<any[]>([]);
  const [selectedBatchId, setSelectedBatchId] = useState<string>("");
  const [isLoadingReconciliation, setIsLoadingReconciliation] = useState(false);

  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const {
    notification,
    showNotification,
    hideNotification,
    showSuccess,
    showError,
  } = useNotifications();

  useEffect(() => {
    const stored = localStorage.getItem("lossRunSessionRecords");
    if (stored) {
      try {
        const parsed: SessionRecord[] = JSON.parse(stored);
        // Only keep records created by handleSubmit (id: record_{timestamp}_{random}).
        // Filters out any history records that leaked in before the Current/History
        // tab split was introduced.
        const sessionOnly = parsed.filter(
          (r) =>
            typeof r.id === "string" &&
            r.id.startsWith("record_") &&
            r.id.split("_").length >= 3,
        );
        setSessionRecords(sessionOnly);
      } catch (e) {
        console.error("Failed to load session records:", e);
      }
    }
  }, []);
  useEffect(() => {
    // Skip saving on initial mount to prevent overwriting localStorage with empty array
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // ONLY save completed and failed records - NOT processing records
    // This prevents "processing" records from being stuck after page reload
    const completedRecords = sessionRecords.filter(
      r => r.status === "completed" || r.status === "failed"
    );

    localStorage.setItem(
      "lossRunSessionRecords",
      JSON.stringify(completedRecords),
    );
  }, [sessionRecords]);

  // Auto-fetch history when switching to History tab
  useEffect(() => {
    if (historyTab === 1 && isAuthenticated) {
      handleLoadHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyTab, isAuthenticated]);

  // Prevent page reload when there are processing records
  useEffect(() => {
    const hasProcessingRecords = sessionRecords.some(r => r.status === "processing");

    if (hasProcessingRecords) {
      const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        // Skip popup when redirecting to login due to session expiry
        if (isRedirectingToLogin.current) return;
        e.preventDefault();
        e.returnValue = "Processing is ongoing. Do you want to reload?";
        return "Processing is ongoing. Do you want to reload?";
      };

      window.addEventListener("beforeunload", handleBeforeUnload);

      return () => {
        window.removeEventListener("beforeunload", handleBeforeUnload);
      };
    }
  }, [sessionRecords]);

  // Periodically check if auth token is still valid - auto redirect to login if expired
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkToken = async () => {
      try {
        await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          },
        });
      } catch (error) {
        console.error("Session expired - redirecting to login");
        showError("Session expired. Redirecting to login...");
        isRedirectingToLogin.current = true;
        setTimeout(() => {
          loginWithRedirect();
        }, 1500);
      }
    };

    // Check token every 5 minutes
    const interval = setInterval(checkToken, 5 * 60 * 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Auto-redirect to Auth0 when invitation parameters are detected
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const invitation = urlParams.get("invitation");
    const organization = urlParams.get("organization");

    if ((invitation || organization) && !isAuthenticated && !authLoading) {
      console.log(
        "🔗 Invitation detected - redirecting to Auth0 for password setup...",
      );
      loginWithRedirect({
        authorizationParams: {
          invitation: invitation || undefined,
          organization: organization || undefined,
        },
      });
    }
  }, [isAuthenticated, authLoading, loginWithRedirect]);

  const handleFilesAdded = (files: File[], replace: boolean = false) => {
    const newFiles = files.map((file) => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      status: "ready" as const,
    }));

    if (replace) {
      setUploadFiles(newFiles);
    } else {
      setUploadFiles((prev) => [...prev, ...newFiles]);
    }
    setUploadError(null);
  };
  const handleFileRemove = (id: string) => {
    setUploadFiles((prev) => prev.filter((f) => f.id !== id));
    setUploadError(null);
  };

  const handleLoadHistory = async (page: number = 1) => {
    setIsLoadingHistory(true);
    try {
      // Get Auth0 access token
      let accessToken: string | undefined;
      if (isAuthenticated) {
        try {
          accessToken = await getAccessTokenSilently({
            authorizationParams: {
              audience: process.env.REACT_APP_AUTH0_AUDIENCE,
            },
          });
          console.log("✓ Got access token for history request");
        } catch (tokenError) {
          console.error("Failed to get access token:", tokenError);
          showError("Session expired. Redirecting to login...");
          setIsLoadingHistory(false);
          isRedirectingToLogin.current = true;
          setTimeout(() => loginWithRedirect(), 1500);
          return;
        }
      }

      const response = await apiService.getHistory(accessToken, page);
      if (!response || !Array.isArray(response.results))
        throw new Error("Expected array of records");

      const records: SessionRecord[] = response.results
        .map(transformToSessionRecord)
        .sort(
          (a, b) =>
            new Date(b.submittedAt).getTime() -
            new Date(a.submittedAt).getTime(),
        );
      setDbRecords(records);
      setPagination(response.pagination);
      setCurrentPage(page);

      setIsLoadingHistory(false);
      showSuccess(`Loaded page ${page} (${records.length} records)`);
    } catch (error: any) {
      showError(error.message || "Failed to load history");
      setIsLoadingHistory(false);
    }
  };

  const handleSubmit = async () => {
    if (uploadFiles.length < 2) {
      setUploadError("Please upload both PDF and JSON files");
      return;
    }
    const pdfFiles = uploadFiles.filter(
      (f) =>
        f.file.type === "application/pdf" ||
        f.file.name.toLowerCase().endsWith(".pdf"),
    );
    const jsonFile = uploadFiles.find(
      (f) =>
        f.file.type === "application/json" ||
        f.file.name.toLowerCase().endsWith(".json"),
    );
    if (pdfFiles.length === 0 || !jsonFile) {
      setUploadError("Please upload at least one PDF and one JSON file");
      return;
    }

    const isBatchUpload = pdfFiles.length > 1;
    const recordId = `record_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const newRecord: SessionRecord = {
      id: recordId,
      pdfFileName: isBatchUpload
        ? `${pdfFiles.length} PDFs (Batch)`
        : pdfFiles[0].file.name,
      jsonFileName: jsonFile.file.name,
      status: "processing",
      submittedAt: new Date().toISOString(),
      isBatch: isBatchUpload,
      policyCount: pdfFiles.length,
    };

    setSessionRecords((prev) => [newRecord, ...prev]);
    setIsUploading(true);
    setUploadError(null);
    setUploadFiles([]);
    showNotification(`Processing ${pdfFiles.length} PDF(s)`, "info");

    // Get Auth0 access token first
    let accessToken: string | undefined;
    if (isAuthenticated) {
      try {
        accessToken = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          },
        });
        console.log("✓ Got access token for API request");
      } catch (tokenError) {
        console.error("Failed to get access token:", tokenError);
        showError("Session expired. Redirecting to login...");
        setIsUploading(false);
        isRedirectingToLogin.current = true;
        setTimeout(() => loginWithRedirect(), 1500);
        return;
      }
    }

    // Unblock UI immediately after files are submitted - allow parallel uploads
    setIsUploading(false);

    try {
      const startTime = Date.now();
      const rawResponse = await apiService.processLossRun(
        pdfFiles.map((f) => f.file),
        jsonFile.file,
        accessToken,
      );
      const duration = (Date.now() - startTime) / 1000;
      const workflowId = rawResponse.workflow_id;
      const batchId = rawResponse.batch_id;
      const policies = extractPolicies(rawResponse);

      // Fetch from History DB to get complete processed data
      let freshRecords: SessionRecord[] = [];
      try {
        const rawRecords = await apiService.getHistory(accessToken);
        if (rawRecords && Array.isArray(rawRecords.results)) {
          freshRecords = rawRecords.results.map(transformToSessionRecord);
          setDbRecords(
            freshRecords.sort(
              (a, b) =>
                new Date(b.submittedAt).getTime() -
                new Date(a.submittedAt).getTime(),
            ),
          );
        }
      } catch (historyError) {
        console.error(
          "Failed to fetch history after processing:",
          historyError,
        );
      }

      // Match DB record by batch_id or workflow_id to get complete data
      setSessionRecords((prev) =>
        prev.map((r) => {
          if (r.id === recordId) {
            // Try to find the matching record from History DB
            const matchedFromHistory = freshRecords.find(
              (fr) =>
                (batchId && fr.batch_id === batchId) ||
                (workflowId && fr.workflow_id === workflowId),
            );

            if (matchedFromHistory) {
              // Use complete data from DB
              return {
                ...matchedFromHistory,
                id: recordId, // Keep session record ID
              };
            }

            // Fallback: use rawResponse data if no match found
            return {
              ...r,
              status: "completed",
              completedAt: new Date().toISOString(),
              duration,
              workflow_id: workflowId,
              batch_id: batchId,
              isBatch: policies.length > 1,
              policyCount: policies.length,
              policies: policies,
              augmentedPolicy: policies[0]?.augmented_policy,
            };
          }
          return r;
        }),
      );
      showSuccess(
        `Processing complete! ${policies.length} policy(ies) processed.`,
      );
    } catch (error: any) {
      setSessionRecords((prev) =>
        prev.map((r) =>
          r.id === recordId
            ? {
              ...r,
              status: "failed",
              completedAt: new Date().toISOString(),
              error: error.message || "Failed",
            }
            : r,
        ),
      );
      setUploadError(error.message);
      showError(error.message);
    }
  };

  const handleViewRecord = async (record: SessionRecord) => {
    if (record.workflow_id) {
      setIsLoadingView(true);
      try {
        const response = await apiService.getHistory();
        const matchingRecord = response.results.find(
          (r: any) => r.workflow_id === record.workflow_id,
        );
        if (matchingRecord) {
          setSelectedRecord(transformToSessionRecord(matchingRecord));
        } else {
          showError("Record not found");
          setIsLoadingView(false);
          return;
        }
      } catch (error) {
        showError("Failed to load record");
        setIsLoadingView(false);
        return;
      }
      setIsLoadingView(false);
    } else {
      setSelectedRecord(record);
    }
    setViewDialogOpen(true);
    setActiveTab(0);
    setSelectedPolicyIndex(0);
  };

  // Dashboard handler - opens LossRunDashboard
  const handleOpenDashboard = async (
    record: SessionRecord,
    policyIndex: number = 0,
  ) => {
    setIsLoadingDashboard(true);
    if (record.workflow_id) {
      try {
        const response = await apiService.getHistory();
        const matchingRecord = response.results.find(
          (r: any) => r.workflow_id === record.workflow_id,
        );
        if (matchingRecord) {
          const freshRecord = transformToSessionRecord(matchingRecord);
          setDashboardRecord(freshRecord);
          setDashboardPolicyIndex(policyIndex);
          setDashboardOpen(true);
        } else {
          showError("Record not found");
        }
      } catch (error) {
        showError("Failed to load dashboard data");
      }
    } else if (record.augmentedPolicy || record.policies) {
      setDashboardRecord(record);
      setDashboardPolicyIndex(policyIndex);
      setDashboardOpen(true);
    }
    setIsLoadingDashboard(false);
  };

  const getDashboardPolicyData = () => {
    if (!dashboardRecord) return null;
    if (dashboardRecord.policies && dashboardRecord.policies.length > 0)
      return dashboardRecord.policies[dashboardPolicyIndex]?.augmented_policy;
    return dashboardRecord.augmentedPolicy;
  };

  const handleDeleteRecord = (recordId: string) => {
    if (historyTab === 0) {
      setSessionRecords((prev) => prev.filter((r) => r.id !== recordId));
    } else {
      setDbRecords((prev) => prev.filter((r) => r.id !== recordId));
    }
    if (selectedRecord?.id === recordId) {
      setViewDialogOpen(false);
      setSelectedRecord(null);
    }
  };
  const handleClearHistory = () => {
    if (window.confirm("Clear all session history?")) {
      setSessionRecords([]);
      localStorage.removeItem("lossRunSessionRecords");
      showNotification("Session history cleared", "info");
    }
  };

  const handleDownload = (record: SessionRecord, policyIndex?: number) => {
    let dataToDownload: any, filename: string;
    if (record.isBatch && record.policies && policyIndex !== undefined) {
      const policy = record.policies[policyIndex];
      dataToDownload = policy.augmented_policy;
      filename =
        policy.suggested_filename || `${policy.appnum}_LossRunOutput.json`;
    } else if (record.isBatch && record.policies) {
      dataToDownload = record.policies.map((p) => p.augmented_policy);
      filename = `batch-${record.workflow_id || record.id}-all.json`;
    } else {
      dataToDownload = record.augmentedPolicy;
      filename = `augmented-policy-${record.id}.json`;
    }
    if (!dataToDownload) return;
    const blob = new Blob([JSON.stringify(dataToDownload, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getStatusColor = (
    status: string,
  ): "success" | "warning" | "error" | "default" => {
    switch (status?.toLowerCase()) {
      case "success":
        return "success";
      case "warning":
        return "warning";
      case "error":
      case "failed":
        return "error";
      default:
        return "default";
    }
  };
  const handleCopyAllPolicies = async () => {
    if (!selectedRecord?.policies) return;
    try {
      await navigator.clipboard.writeText(
        JSON.stringify(
          selectedRecord.policies.map((p) => ({
            appnum: p.appnum,
            status: p.status,
            augmented_policy: p.augmented_policy,
          })),
          null,
          2,
        ),
      );
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleAnalyzeResults = async (record: SessionRecord) => {
    if (!record) return;
    let recordToAnalyze = record;
    if (record.workflow_id) {
      try {
        const response = await apiService.getHistory();
        const matchingRecord = response.results.find(
          (r: any) => r.workflow_id === record.workflow_id,
        );
        if (matchingRecord)
          recordToAnalyze = transformToSessionRecord(matchingRecord);
      } catch (error) {
        console.error("Failed to fetch for analysis:", error);
      }
    }

    try {
      const isNewRecord =
        analysisThreadId === null ||
        analysisSelectedRecord?.id !== recordToAnalyze.id;
      let threadId = analysisThreadId;

      if (isNewRecord) {
        threadId = Math.floor(Math.random() * 1000000000);
        setAnalysisThreadId(threadId);
        setConversationHistory([]);
        setAnalysisSelectedRecord(recordToAnalyze);
        setIsAnalyzing(true);
        setAnalysisDialogOpen(true);

        let summaryJson: string, analysisPrompt: string;
        if (
          recordToAnalyze.isBatch &&
          recordToAnalyze.policies &&
          recordToAnalyze.policies.length > 1
        ) {
          summaryJson = JSON.stringify(
            createBatchAnalysisSummary(recordToAnalyze),
            null,
            2,
          );
          analysisPrompt = `You are an expert insurance underwriter triaging ${recordToAnalyze.policies.length} policies. Present an actionable report.\n\nDATA:\n${summaryJson}\n\nUse bullet points, focus on actionable insights.`;
        } else {
          const policyToAnalyze =
            recordToAnalyze.policies?.[0]?.augmented_policy ||
            recordToAnalyze.augmentedPolicy;
          if (!policyToAnalyze) throw new Error("No policy data available");
          summaryJson = JSON.stringify(
            createAnalysisSummary(policyToAnalyze),
            null,
            2,
          );
          analysisPrompt = `You are an expert insurance underwriter. Present a clear summary.\n\nDATA:\n${summaryJson}\n\nUse bullet points, currency as $X,XXX.`;
        }

        const response = await fetch(API_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            agent_id: "328e1efd-c171-4365-8a7e-00f277e142e5",
            message: analysisPrompt,
            thread_id: threadId,
          }),
        });
        if (!response.ok)
          throw new Error(`Analysis failed: ${response.status}`);
        const result = await response.json();
        let agentResponse = (
          result.response ||
          result.message ||
          JSON.stringify(result, null, 2)
        )
          .replace(/\*\*/g, "")
          .replace(/\*/g, "")
          .replace(/#{1,6}\s/g, "")
          .replace(/`{1,3}/g, "")
          .trim();
        setConversationHistory([{ role: "assistant", content: agentResponse }]);
        setIsAnalyzing(false);
      } else {
        setAnalysisDialogOpen(true);
      }
    } catch (error) {
      setConversationHistory([
        {
          role: "assistant",
          content: `Analysis Error\n\n${error instanceof Error ? error.message : "Unknown error"}`,
        },
      ]);
      setIsAnalyzing(false);
    }
  };

  const handleFollowUpQuestion = async () => {
    if (
      !followUpQuestion.trim() ||
      !analysisThreadId ||
      !analysisSelectedRecord
    )
      return;
    const userMessage = followUpQuestion.trim();
    setFollowUpQuestion("");
    setConversationHistory((prev) => [
      ...prev,
      { role: "user", content: userMessage },
    ]);
    setIsAnalyzing(true);

    try {
      let contextMessage = userMessage;
      if (
        analysisSelectedRecord.policies?.[0]?.augmented_policy ||
        analysisSelectedRecord.augmentedPolicy
      ) {
        const fullPolicyData =
          analysisSelectedRecord.policies?.[0]?.augmented_policy ||
          analysisSelectedRecord.augmentedPolicy;
        contextMessage = `FOLLOW-UP:\n\n${userMessage}\n\nPOLICY DATA:\n${JSON.stringify(fullPolicyData, null, 2)}`;
      }

      const response = await fetch(API_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          agent_id: "328e1efd-c171-4365-8a7e-00f277e142e5",
          message: contextMessage,
          thread_id: analysisThreadId,
        }),
      });
      if (!response.ok) throw new Error(`Follow-up failed: ${response.status}`);
      const result = await response.json();
      let agentResponse = (result.response || result.message || "")
        .replace(/\*\*/g, "")
        .replace(/\*/g, "")
        .replace(/#{1,6}\s/g, "")
        .replace(/`{1,3}/g, "")
        .trim();
      setConversationHistory((prev) => [
        ...prev,
        { role: "assistant", content: agentResponse },
      ]);
    } catch (error) {
      setConversationHistory((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `Error: ${error instanceof Error ? error.message : "Unknown"}`,
        },
      ]);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleOpenReconciliationReports = async (record: SessionRecord) => {
    if (!record.batch_id) {
      showError("No batch ID found for this record");
      return;
    }

    setSelectedBatchId(record.batch_id);
    setIsLoadingReconciliation(true);
    setReconciliationDialogOpen(true);

    try {
      // Get Auth0 access token
      let accessToken: string | undefined;
      if (isAuthenticated) {
        try {
          accessToken = await getAccessTokenSilently({
            authorizationParams: {
              audience: process.env.REACT_APP_AUTH0_AUDIENCE,
            },
          });
          console.log("✓ Got access token for reconciliation reports request");
        } catch (tokenError) {
          console.error("Failed to get access token:", tokenError);
          showError("Session expired. Redirecting to login...");
          setIsLoadingReconciliation(false);
          isRedirectingToLogin.current = true;
          setTimeout(() => loginWithRedirect(), 1500);
          return;
        }
      }

      const response = await apiService.getReconciliationReports(
        record.batch_id,
        accessToken,
      );
      setReconciliationReports(response.files || []);

      if (response.files && response.files.length === 0) {
        showNotification(
          "No reconciliation reports found for this batch",
          "info",
        );
      }
    } catch (error: any) {
      showError(error.message || "Failed to load reconciliation reports");
      setReconciliationReports([]);
    } finally {
      setIsLoadingReconciliation(false);
    }
  };

  const handleDownloadReconciliationReport = async (filename: string) => {
    try {
      // Get Auth0 access token
      let accessToken: string | undefined;
      if (isAuthenticated) {
        try {
          accessToken = await getAccessTokenSilently({
            authorizationParams: {
              audience: process.env.REACT_APP_AUTH0_AUDIENCE,
            },
          });
          console.log("✓ Got access token for report download");
        } catch (tokenError) {
          console.error("Failed to get access token:", tokenError);
          showError("Session expired. Redirecting to login...");
          isRedirectingToLogin.current = true;
          setTimeout(() => loginWithRedirect(), 1500);
          return;
        }
      }

      showNotification("Downloading report...", "info");
      await apiService.downloadReconciliationReport(
        selectedBatchId,
        filename,
        accessToken,
      );
      showSuccess("Report downloaded successfully");
    } catch (error: any) {
      showError(error.message || "Failed to download report");
    }
  };

  const getFollowUpSuggestions = () => {
    const suggestions: Array<{ label: string; question: string }> = [];
    const policies = analysisSelectedRecord?.policies;
    if (analysisSelectedRecord?.isBatch && policies && policies.length > 1) {
      policies.slice(0, 3).forEach((policy: any) =>
        suggestions.push({
          label: `Details: ${policy.appnum}`,
          question: `Show me detailed analysis for policy ${policy.appnum}.`,
        }),
      );
      suggestions.push({
        label: "Compare all",
        question: "Compare loss ratios across all policies.",
      });
    } else {
      suggestions.push({
        label: "Explain claim provenance",
        question: "Explain the provenance for the largest claim.",
      });
      suggestions.push({
        label: "Loss ratio calculation",
        question: "How are loss ratios calculated?",
      });
    }
    return suggestions;
  };

  const formatDuration = (s?: number) => {
    if (!s) return "-";
    if (s < 60) return `${s.toFixed(1)}s`;
    return `${Math.floor(s / 60)}m ${Math.floor(s % 60)}s`;
  };
  const formatTimestamp = (iso: string) => {
    const d = new Date(iso);
    return isMobile
      ? d.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "America/New_York"
      })
      : d.toLocaleTimeString([], {
        timeZone: "America/New_York"
      });
  };
  const truncateFileName = (n: string, max: number = 20) => {
    if (n.length <= max) return n;
    const ext = n.split(".").pop();
    return `${n.substring(0, max - 5)}...${ext}`;
  };
  const getCurrentPolicyData = () => {
    if (!selectedRecord) return null;
    if (selectedRecord.policies && selectedRecord.policies.length > 0)
      return selectedRecord.policies[selectedPolicyIndex]?.augmented_policy;
    return selectedRecord.augmentedPolicy;
  };
  const getViewTabs = () =>
    selectedRecord?.isBatch &&
      selectedRecord.policies &&
      selectedRecord.policies.length > 1
      ? ["Tree View", "Raw JSON", "All Policies"]
      : ["Tree View", "Raw JSON"];

  // ✅ STEP 2 HELPER: Get Backend Runtime
  const getPolicyTotalRuntime = (record?: SessionRecord) => {
    if (!record?.Policy_Timing || record.Policy_Timing.length === 0)
      return undefined;
    // If we have timing data, take the MAX total runtime (safe for batches)
    const values = record.Policy_Timing.map(
      (p: any) => p.TOTAL_POLICY_RUNTIME_SECONDS,
    ).filter((v: any) => typeof v === "number");

    if (values.length === 0) return null;
    return Math.max(...values);
  };

  if (authLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#0F172A",
          color: "white",
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress sx={{ color: "primary.main", mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Loading Authentication...
          </Typography>
        </Box>
      </Box>
    );
  }
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          bgcolor: isAuthenticated ? "#F8FAFC" : "#0F172A",
        }}
      >
        {/* Header */}
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            bgcolor: "#2145EA",
            borderBottom: "1px solid #E2E8F0",
            zIndex: 1201,
          }}
        >
          <Toolbar
            sx={{ minHeight: "64px", px: { xs: 2, md: 4 }, display: "flex" }}
          >
            {/* Left Section - Branded Logo */}
            {/* <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar variant="rounded" sx={{ bgcolor: isAuthenticated ? 'primary.main' : '#10B981', width: 32, height: 32, fontWeight: 'bold', fontSize: '1rem' }}>
                <MagicWandIcon sx={{ color: 'white', fontSize: '1.2rem' }} />
              </Avatar>
              <Typography variant="h6" sx={{ color: isAuthenticated ? 'text.primary' : 'white', fontWeight: 700, letterSpacing: '-0.02em', display: { xs: 'none', sm: 'block' } }}>Elevatenow</Typography>
            </Box> */}

            <Box
              component="img"
              src="/Elevatenowwhitelogo.svg"
              alt="ElevateNow Logo"
              sx={{
                height: { xs: 20, sm: 28 },
                width: "auto",
                objectFit: "contain",
              }}
            />

            {!isAuthenticated && (
              <Box sx={{ flex: 2, display: "flex", justifyContent: "center" }}>
                <Typography
                  variant="h6"
                  sx={{ color: "white", fontWeight: 700 }}
                >
                  Loss Run Processing
                </Typography>
              </Box>
            )}

            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: { xs: 1, sm: 3 },
              }}
            >
              {!isAuthenticated && (
                <Button
                  component="a"
                  href="/samples/QuickReference.pdf"
                  target="_blank"
                  startIcon={<QuickRefIcon />}
                  variant="outlined"
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    color: "primary.main",
                    backgroundColor: "white",

                    "&:hover": {
                      backgroundColor: "white",
                    },
                  }}
                >
                  {isMobile ? "Guide" : "Quick Reference"}
                </Button>
              )}
              {authLoading ? (
                <CircularProgress size={24} sx={{ color: "primary.main" }} />
              ) : isAuthenticated ? (
                <>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#e7e7e8",
                      fontWeight: 600,
                      display: { xs: "none", lg: "block" },
                      fontSize: "0.8rem",
                    }}
                  >
                    {user?.email || user?.name || "vs@artifidata.ai"}
                  </Typography>
                  <Button
                    onClick={() =>
                      logout({
                        logoutParams: { returnTo: window.location.origin },
                      })
                    }
                    variant="contained"
                    endIcon={<LogoutIcon fontSize="small" />}
                    sx={{
                      bgcolor: "#ffffff",
                      color: "#0F172A",
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "0.875rem",
                      borderRadius: "8px",
                      boxShadow: "none",
                      border: "1px solid #E2E8F0",
                      "&:hover": {
                        bgcolor: "#ffffff",
                        boxShadow: "none",
                      },
                    }}
                  >
                    Logout
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => loginWithRedirect()}
                  startIcon={<LoginIcon />}
                  variant="contained"
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    bgcolor: "#1a56db",
                    "&:hover": { bgcolor: "#1a56db" },
                  }}
                >
                  Login
                </Button>
              )}
            </Box>
          </Toolbar>
        </AppBar>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            pt: "64px",
            bgcolor: "#F8FAFC",
          }}
        >
          {!isAuthenticated ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                px: { xs: 2, md: 4 },
                p: 4,
              }}
            >
              <Card
                sx={{
                  maxWidth: 600,
                  width: "100%",
                  p: { xs: 4, md: 8 },
                  textAlign: "center",
                  bgcolor: "#0F172A",
                  borderRadius: 4,
                  boxShadow:
                    "0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 10px 10px -5px rgba(0, 0, 0, 0.08)",
                  border: "1px solid rgba(0, 0, 0, 0.06)",
                }}
              >
                <AccountIcon
                  sx={{ fontSize: 60, color: "#1a56db", mb: 2 }}
                />

                <Typography
                  variant="h5"
                  sx={{ color: "white", mb: 1, fontWeight: 700 }}
                >
                  Authentication Required
                </Typography>
                <Typography
                  sx={{ color: "#94A3B8", mb: 4, fontSize: "0.95rem" }}
                >
                  Please log in to access Loss Run Processing
                </Typography>
                <Button
                  onClick={() => loginWithRedirect()}
                  variant="contained"
                  size="large"
                  startIcon={<LoginIcon />}
                  sx={{
                    bgcolor: "#1a56db",
                    "&:hover": { bgcolor: "#1a56db" },
                  }}
                >
                  Log In to Continue
                </Button>
              </Card>
            </Box>
          ) : (
            <Box
              sx={{
                width: "100%",
                maxWidth: "1800px",
                mx: "auto",
                display: "flex",
                flexDirection: "column",
                px: { xs: 4, md: 8, lg: 12 },
                py: 6,
              }}
            >
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h4"
                  sx={{
                    color: "text.primary",
                    fontWeight: 800,
                    letterSpacing: "-0.02em",
                    fontFamily: "Inter, Arial, sans-serif",
                    mb: 1,
                  }}
                >
                  Loss Run Processing
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "text.secondary", fontWeight: 500 }}
                >
                  Upload documents to analyze risk and carrier history.
                </Typography>
              </Box>

              <Box sx={{ display: "flex", flexDirection: { xs: "column-reverse", md: "row" }, gap: 4, alignItems: "flex-start" }}>
                <Box
                  component="aside"
                  sx={{
                    width: { xs: "100%", md: 380 },
                    flexShrink: 0,
                  }}
                >
                  <UploadManager
                    files={uploadFiles}
                    onFilesAdded={handleFilesAdded}
                    onFileRemove={handleFileRemove}
                    onSubmit={handleSubmit}
                    isUploading={isUploading}
                    error={uploadError}
                  />
                </Box>

                <Box
                  component="main"
                  sx={{ flex: 1, display: "flex", flexDirection: "column" }}
                >
                  <Card
                    sx={{
                      flex: 1,
                      bgcolor: "#ffffff",
                      borderRadius: 4,
                      boxShadow:
                        "0 10px 15px -3px rgba(0, 0, 0, 0.12), 0 4px 6px -2px rgba(0, 0, 0, 0.08)",

                      border: "1px solid #E5E7EB",

                      p: 4,
                      mt: 0,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 4,
                        flexWrap: "wrap",
                        gap: 2,
                      }}
                    >
                      <Tabs
                        value={historyTab}
                        onChange={(_, v) => setHistoryTab(v)}
                        sx={{
                          mb: { xs: 2, sm: 0 },
                          "& .MuiTab-root": {
                            fontWeight: 700,
                            fontSize: "0.875rem",
                            px: 2,
                            minHeight: 48,
                          },
                        }}
                      >
                        <Tab label={`CURRENT (${sessionRecords.length})`} />
                        <Tab label={`HISTORY (${pagination?.total_count || 0})`} />
                      </Tabs>
                      <Box
                        sx={{ display: "flex", gap: 2, alignItems: "center" }}
                      >
                        <TextField
                          size="small"
                          placeholder="Search batches..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          sx={{
                            bgcolor: "#F8FAFC",
                            width: { xs: "100%", sm: 280 },
                            border: "1px solid rgb(15 23 42 / 50%)",
                            borderRadius: "10px",

                            "& .MuiOutlinedInput-root": {
                              borderRadius: "10px",

                              "&:hover fieldset": {
                                border: "none",
                              },
                            },
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon
                                  fontSize="small"
                                  sx={{ color: "#94A3B8", ml: 0.5 }}
                                />
                              </InputAdornment>
                            ),
                          }}
                        />
                        {historyTab === 0 && (
                          <Tooltip title="Refresh Status - Check for completed jobs">
                            <IconButton
                              onClick={() => handleLoadHistory()}
                              disabled={isLoadingHistory}
                              sx={{
                                border: "1px solid #E2E8F0",
                                borderRadius: 2,
                                bgcolor: "white",
                                width: 40,
                                height: 40,
                                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                                "&:hover": { bgcolor: "#F1F5F9" },
                              }}
                            >
                              {isLoadingHistory ? (
                                <CircularProgress size={20} />
                              ) : (
                                <CachedIcon
                                  fontSize="small"
                                  sx={{ color: "#64748B" }}
                                />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                        {historyTab === 1 && (
                          <Tooltip title="Refresh History">
                            <IconButton
                              onClick={() => handleLoadHistory()}
                              disabled={isLoadingHistory}
                              sx={{
                                border: "1px solid #E2E8F0",
                                borderRadius: 2,
                                bgcolor: "white",
                                width: 40,
                                height: 40,
                                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                                "&:hover": { bgcolor: "#F1F5F9" },
                              }}
                            >
                              {isLoadingHistory ? (
                                <CircularProgress size={20} />
                              ) : (
                                <CachedIcon
                                  fontSize="small"
                                  sx={{ color: "#64748B" }}
                                />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>

                    <TableContainer
                      sx={{
                        border: "1px solid #F1F5F9",
                        borderRadius: 3,
                        overflow: "hidden",
                      }}
                    >
                      <Table stickyHeader>
                        <TableHead>
                          <TableRow sx={{ bgcolor: "#E2E8F0" }}>
                            <TableCell
                              sx={{
                                pl: 4,
                                pt: 3,
                                pb: 2,
                                fontWeight: 800,
                                color: "#475569",
                                fontSize: "0.65rem",
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                                border: "none",
                                bgcolor: "#E2E8F0",
                              }}
                            >
                              STATUS
                            </TableCell>
                            <TableCell
                              sx={{
                                pt: 3,
                                pb: 2,
                                fontWeight: 800,
                                color: "#475569",
                                fontSize: "0.65rem",
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                                border: "none",
                                bgcolor: "#E2E8F0",
                              }}
                            >
                              BATCH / FILES
                            </TableCell>
                            <TableCell
                              sx={{
                                pt: 3,
                                pb: 2,
                                fontWeight: 800,
                                color: "#475569",
                                fontSize: "0.65rem",
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                                border: "none",
                                bgcolor: "#E2E8F0",
                              }}
                            >
                              Time
                            </TableCell>
                            <TableCell
                              sx={{
                                pt: 3,
                                pb: 2,
                                fontWeight: 800,
                                color: "#475569",
                                fontSize: "0.65rem",
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                                border: "none",
                                bgcolor: "#E2E8F0",
                              }}
                            >
                              Duration
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                pr: 4,
                                pt: 3,
                                pb: 2,
                                fontWeight: 800,
                                color: "#475569",
                                fontSize: "0.65rem",
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                                border: "none",
                                bgcolor: "#E2E8F0",
                              }}
                            >
                              ACTIONS
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell
                              colSpan={3}
                              sx={{ p: 0, borderBottom: "1px solid #F1F5F9" }}
                            />
                          </TableRow>
                          {filteredRecords.length === 0 ? (
                            <TableRow>
                              <TableCell
                                colSpan={5}
                                align="center"
                                sx={{ py: 8 }}
                              >
                                <Typography color="text.secondary">
                                  {searchTerm
                                    ? "No matching batches found."
                                    : historyTab === 1
                                      ? "No processing activity yet. Click Refresh to load..."
                                      : "No processing activity yet."}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            filteredRecords.map((record) => {
                              const mainPolicy =
                                record.policies?.[0]?.augmented_policy ||
                                record.augmentedPolicy ||
                                {};
                              const jsonData =
                                mainPolicy.jsonData || mainPolicy;
                              const appInfo = jsonData.Application || {};
                              const generalInfo = jsonData.GeneralInfo || {};
                              const insuredName =
                                generalInfo.entityFullName ||
                                jsonData.SubmissionInfo?.txtInsuredName ||
                                appInfo.InsuredFullName ||
                                (record.isBatch
                                  ? "Multiple Insureds"
                                  : "Unknown");

                              return (
                                <TableRow
                                  key={record.id}
                                  hover
                                  sx={{
                                    "&:last-child td, &:last-child th": {
                                      border: 0,
                                    },
                                  }}
                                >
                                  <TableCell sx={{ pl: 4 }}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5,
                                      }}
                                    >
                                      {record.status === "processing" ? (
                                        <Box>
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 1,
                                            }}
                                          >
                                            <CircularProgress
                                              size={12}
                                              thickness={6}
                                              sx={{ color: "#0558FD" }}
                                            />
                                            <Chip
                                              label="PROCESSING..."
                                              size="small"
                                              sx={{
                                                fontWeight: 800,
                                                fontSize: "0.65rem",
                                                borderRadius: "6px",
                                                bgcolor: "#DBEAFE",
                                                color: "#1E40AF",
                                                border: "none",
                                                height: 20,
                                                letterSpacing: "0.03em",
                                              }}
                                            />
                                          </Box>
                                          {/* <Typography
                                            variant="caption"
                                            sx={{
                                              display: "block",
                                              mt: 0.5,
                                              color: "#94A3B8",
                                              fontWeight: 500,
                                              pl: 1,
                                            }}
                                          >
                                            Time-{" "}
                                            {formatTimestamp(
                                              record.submittedAt,
                                            )}
                                          </Typography> */}
                                        </Box>
                                      ) : (
                                        <Box>
                                          <Chip
                                            label={record.status.toUpperCase()}
                                            size="small"
                                            sx={{
                                              fontWeight: 700,
                                              fontSize: "0.65rem",
                                              borderRadius: "6px",
                                              bgcolor:
                                                record.status === "completed"
                                                  ? "#DCFCE7"
                                                  : record.status === "failed"
                                                    ? "#FEE2E2"
                                                    : "#FEF3C7",
                                              color:
                                                record.status === "completed"
                                                  ? "#166534"
                                                  : record.status === "failed"
                                                    ? "#991B1B"
                                                    : "#92400E",
                                              border: "none",
                                              height: 20,
                                            }}
                                          />
                                          {/* <Typography
                                            variant="caption"
                                            sx={{
                                              display: "block",
                                              mt: 0.5,
                                              color: "#94A3B8",
                                              fontWeight: 500,
                                            }}
                                          >
                                            Today,{" "}
                                            {formatTimestamp(
                                              record.submittedAt,
                                            )}
                                          </Typography> */}
                                        </Box>
                                      )}
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Box>
                                      <Typography
                                        sx={{
                                          fontWeight: 700,
                                          color: "#0F172A",
                                          fontSize: "0.875rem",
                                        }}
                                      >
                                        {record.batch_id
                                          ? `${record.batch_id.substring(0, 8)}`
                                          : record.isBatch
                                            ? "Batch Processing"
                                            : truncateFileName(
                                              record.pdfFileName,
                                            )}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: "#64748B",
                                          fontWeight: 500,
                                        }}
                                      >
                                        {record.policyCount ||
                                          (record.isBatch ? 2 : 1)}{" "}
                                        PDF File
                                        {(record.policyCount ||
                                          (record.isBatch ? 2 : 1)) !== 1
                                          ? "s"
                                          : ""}{" "}
                                        • {record.jsonFileName || "policy.json"}
                                      </Typography>
                                    </Box>
                                  </TableCell>

                                  <TableCell>
                                    <Typography
                                      sx={{
                                        color: "#0F172A",
                                        fontWeight: 500,
                                        fontSize: "0.875rem",
                                      }}
                                    >
                                      {formatTimestamp(record.submittedAt)}
                                    </Typography>
                                  </TableCell>

                                  {!isMobile && (
                                    <TableCell>
                                      {record.status !== "processing" &&
                                        getPolicyTotalRuntime(record) ? (
                                        <Typography
                                          sx={{
                                            color: "#17af55",
                                            fontWeight: 600,
                                            cursor: "pointer",
                                            textDecoration: "underline",
                                            fontSize: "0.875rem",
                                          }}
                                          onClick={() => {
                                            setTimingRecord(record);
                                            setTimingOpen(true);
                                          }}
                                        >
                                          {formatDuration(
                                            getPolicyTotalRuntime(record) ??
                                            undefined,
                                          )}
                                        </Typography>
                                      ) : (
                                        <Typography
                                          sx={{
                                            color: "#0F172A",
                                            fontSize: "0.875rem",
                                            fontWeight: 500,
                                          }}
                                        >
                                          {formatDuration(record?.duration)}
                                        </Typography>
                                      )}
                                    </TableCell>
                                  )}
                                  <TableCell align="right" sx={{ pr: 4 }}>
                                    {record.status === "processing" ? (
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: "#94A3B8",
                                          fontWeight: 600,
                                          fontStyle: "italic",
                                          pr: 2,
                                        }}
                                      >
                                        Awaiting results
                                      </Typography>
                                    ) : (
                                      <Stack
                                        direction="row"
                                        spacing={0.5}
                                        justifyContent="flex-end"
                                      >
                                        {record.status === "completed" && (
                                          <>
                                            {record.status === "completed" &&
                                              record.batch_id && (
                                                <Tooltip title="Reconciliation Reports">
                                                  <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                      handleOpenReconciliationReports(
                                                        record,
                                                      )
                                                    }
                                                    sx={{
                                                      color: "#9c27b0",
                                                      transition: "color 0.2s",
                                                      "&:hover": {
                                                        bgcolor:
                                                          "rgba(156, 39, 176, 0.1)",
                                                      },
                                                    }}
                                                  >
                                                    <ReportIcon
                                                      sx={{
                                                        fontSize: "1.1rem",
                                                      }}
                                                    />
                                                  </IconButton>
                                                </Tooltip>
                                              )}
                                            <Tooltip title="Open Loss Run Dashboard">
                                              <IconButton
                                                size="small"
                                                onClick={() =>
                                                  handleOpenDashboard(record)
                                                }
                                                disabled={isLoadingDashboard}
                                                sx={{
                                                  color: "#ff9800",
                                                  transition: "color 0.2s",
                                                  "&:hover": {
                                                    bgcolor:
                                                      "rgba(255, 152, 0, 0.1)",
                                                  },
                                                }}
                                              >
                                                {isLoadingDashboard ? (
                                                  <CircularProgress
                                                    size={16}
                                                    color="inherit"
                                                  />
                                                ) : (
                                                  <DashboardIcon
                                                    sx={{ fontSize: "1rem" }}
                                                  />
                                                )}
                                              </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Download Output JSON">
                                              <IconButton
                                                size="small"
                                                onClick={() =>
                                                  handleDownload(record)
                                                }
                                                sx={{
                                                  color: "success.main",
                                                  transition: "color 0.2s",
                                                  "&:hover": {
                                                    bgcolor:
                                                      "rgba(22, 101, 52, 0.1)",
                                                  },
                                                }}
                                              >
                                                <DownloadIcon
                                                  sx={{ fontSize: "1.1rem" }}
                                                />
                                              </IconButton>
                                            </Tooltip>
                                            <Tooltip title="View JSON Results">
                                              <IconButton
                                                size="small"
                                                onClick={() =>
                                                  handleViewRecord(record)
                                                }
                                                disabled={isLoadingView}
                                                sx={{
                                                  color: "#1a56db",
                                                  transition: "color 0.2s",
                                                  "&:hover": {
                                                    bgcolor:
                                                      "rgba(23, 175, 85, 0.1)",
                                                  },
                                                }}
                                              >
                                                {isLoadingView &&
                                                  selectedRecord?.id ===
                                                  record.id ? (
                                                  <CircularProgress size={16} />
                                                ) : (
                                                  <ViewIcon
                                                    sx={{ fontSize: "1.1rem" }}
                                                  />
                                                )}
                                              </IconButton>
                                            </Tooltip>


                                          </>
                                        )}
                                        <Tooltip title="Delete Record">
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              handleDeleteRecord(record.id)
                                            }
                                            sx={{
                                              color: "error.main",
                                              transition: "color 0.2s",
                                              "&:hover": {
                                                bgcolor:
                                                  "rgba(220, 38, 38, 0.1)",
                                              },
                                            }}
                                          >
                                            <DeleteIcon
                                              sx={{ fontSize: "1.1rem" }}
                                            />
                                          </IconButton>
                                        </Tooltip>
                                      </Stack>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {historyTab === 1 && pagination && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          px: 3,
                          py: 2,
                          borderTop: "1px solid #F1F5F9",
                          bgcolor: "#F8FAFC",
                          borderBottomLeftRadius: 16,
                          borderBottomRightRadius: 16,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#64748B",
                            fontWeight: 500,
                            fontSize: "0.875rem"
                          }}
                        >
                          Showing <Box component="span" sx={{ fontWeight: 700, color: "#0F172A" }}>{dbRecords.length}</Box> of <Box component="span" sx={{ fontWeight: 700, color: "#0F172A" }}>{pagination.total_count}</Box> results
                        </Typography>

                        <Stack direction="row" spacing={1} alignItems="center">
                          <Pagination
                            count={pagination.total_pages}
                            page={pagination.current_page}
                            onChange={(e, p) => handleLoadHistory(p)}
                            shape="rounded"
                            renderItem={(item) => (
                              <PaginationItem
                                slots={{ previous: ChevronLeft, next: ChevronRight }}
                                {...item}
                                sx={{
                                  fontWeight: 600,
                                  border: item.type === 'page' ? '1px solid #E2E8F0' : 'none',
                                  fontSize: '0.875rem',
                                  bgcolor: item.selected ? '#1a56db !important' : 'white',
                                  color: item.selected ? 'white !important' : '#1E293B',
                                  '&:hover': {
                                    bgcolor: item.selected ? '#1a56db' : '#F1F5F9',
                                    borderColor: '#CBD5E1'
                                  },
                                  ...(item.type === 'previous' || item.type === 'next' ? {
                                    border: '1px solid #E2E8F0',
                                    bgcolor: 'white',
                                    mx: 0.5,
                                    '&:hover': {
                                      bgcolor: '#F1F5F9',
                                      borderColor: '#CBD5E1'
                                    }
                                  } : {})
                                }}
                              />
                            )}
                          />
                        </Stack>
                      </Box>
                    )}
                  </Card>
                </Box>
              </Box>
            </Box>
          )}

          <NotificationSnackbar
            notification={notification}
            onClose={hideNotification}
          />

          <PolicyTimingDialog
            open={timingOpen}
            onClose={() => setTimingOpen(false)}
            record={timingRecord}
          />

          {/* View Dialog */}
          <Dialog
            open={viewDialogOpen}
            onClose={() => setViewDialogOpen(false)}
            maxWidth="lg"
            fullWidth
            fullScreen={isMobile}
          >
            <DialogTitle sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
                  >
                    Results
                  </Typography>
                  {selectedRecord?.isBatch && (
                    <Chip
                      icon={<BatchIcon />}
                      label={`${selectedRecord.policyCount} Policies`}
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                  )}
                </Box>
                <IconButton
                  onClick={() => setViewDialogOpen(false)}
                  size="small"
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>

            <DialogContent sx={{ p: { xs: 1, sm: 2 } }}>
              {selectedRecord && (
                <>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      PDF: {selectedRecord.pdfFileName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      JSON: {selectedRecord.jsonFileName}
                    </Typography>
                    {selectedRecord.workflow_id && (
                      <Typography variant="body2" color="text.secondary" noWrap>
                        Workflow: {selectedRecord.workflow_id}
                      </Typography>
                    )}
                  </Box>

                  {selectedRecord.policies &&
                    selectedRecord.policies.length > 1 &&
                    activeTab !== 2 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mb: 1, display: "block" }}
                        >
                          Select Policy:
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          flexWrap="wrap"
                          useFlexGap
                        >
                          {selectedRecord.policies.map((policy, index) => (
                            <Chip
                              key={policy.appnum || index}
                              label={
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  <span>
                                    {policy.appnum || `Policy ${index + 1}`}
                                  </span>
                                  <Chip
                                    label={policy.status}
                                    size="small"
                                    color={getStatusColor(policy.status || "")}
                                    sx={{
                                      height: 16,
                                      fontSize: "0.6rem",
                                      ml: 0.5,
                                    }}
                                  />
                                </Box>
                              }
                              onClick={() => setSelectedPolicyIndex(index)}
                              color={
                                selectedPolicyIndex === index
                                  ? "primary"
                                  : "default"
                              }
                              variant={
                                selectedPolicyIndex === index
                                  ? "filled"
                                  : "outlined"
                              }
                              size="small"
                              sx={{ mb: 0.5 }}
                            />
                          ))}
                        </Stack>
                      </Box>
                    )}

                  <Tabs
                    value={activeTab}
                    onChange={(e, v) => setActiveTab(v)}
                    variant={isMobile ? "fullWidth" : "standard"}
                  >
                    {getViewTabs().map((label, i) => (
                      <Tab key={label} label={label} />
                    ))}
                  </Tabs>

                  <Box sx={{ mt: 2 }}>
                    {activeTab === 0 && getCurrentPolicyData() && (
                      <Paper
                        sx={{
                          p: { xs: 1, sm: 2 },
                          bgcolor: "background.default",
                          maxHeight: { xs: "calc(100vh - 350px)", sm: "500px" },
                          overflow: "auto",
                        }}
                      >
                        <JsonTreeViewer data={getCurrentPolicyData()} />
                      </Paper>
                    )}
                    {activeTab === 1 && getCurrentPolicyData() && (
                      <Paper
                        sx={{
                          p: { xs: 1, sm: 2 },
                          bgcolor: "background.default",
                          maxHeight: { xs: "calc(100vh - 350px)", sm: "500px" },
                          overflow: "auto",
                        }}
                      >
                        <pre
                          style={{
                            margin: 0,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            fontSize: isMobile ? "0.7rem" : "0.875rem",
                          }}
                        >
                          {JSON.stringify(getCurrentPolicyData(), null, 2)}
                        </pre>
                      </Paper>
                    )}
                    {activeTab === 2 &&
                      selectedRecord.isBatch &&
                      selectedRecord.policies && (
                        <Paper
                          sx={{
                            p: { xs: 1, sm: 2 },
                            bgcolor: "background.default",
                            maxHeight: {
                              xs: "calc(100vh - 350px)",
                              sm: "500px",
                            },
                            overflow: "auto",
                          }}
                        >
                          <Box
                            sx={{
                              mb: 2,
                              pb: 1,
                              borderBottom: "1px solid",
                              borderColor: "divider",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: 1,
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                color="primary"
                                fontWeight="bold"
                              >
                                {selectedRecord.policies.length} Policies in
                                Batch
                              </Typography>
                              <Stack direction="row" spacing={1}>
                                <Button
                                  size="small"
                                  variant={
                                    allPoliciesViewMode === "tree"
                                      ? "contained"
                                      : "outlined"
                                  }
                                  onClick={() => setAllPoliciesViewMode("tree")}
                                >
                                  Tree
                                </Button>
                                <Button
                                  size="small"
                                  variant={
                                    allPoliciesViewMode === "raw"
                                      ? "contained"
                                      : "outlined"
                                  }
                                  onClick={() => setAllPoliciesViewMode("raw")}
                                >
                                  Raw
                                </Button>
                                {allPoliciesViewMode === "raw" && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color={copySuccess ? "success" : "primary"}
                                    startIcon={<CopyIcon />}
                                    onClick={handleCopyAllPolicies}
                                  >
                                    {copySuccess ? "Copied!" : "Copy"}
                                  </Button>
                                )}
                              </Stack>
                            </Box>
                          </Box>
                          {allPoliciesViewMode === "tree" && (
                            <JsonTreeViewer
                              data={selectedRecord.policies.map((p) => ({
                                appnum: p.appnum,
                                status: p.status,
                                augmented_policy: p.augmented_policy,
                              }))}
                            />
                          )}
                          {allPoliciesViewMode === "raw" && (
                            <pre
                              style={{
                                margin: 0,
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                                fontSize: isMobile ? "0.7rem" : "0.875rem",
                              }}
                            >
                              {JSON.stringify(
                                selectedRecord.policies.map((p) => ({
                                  appnum: p.appnum,
                                  status: p.status,
                                  augmented_policy: p.augmented_policy,
                                })),
                                null,
                                2,
                              )}
                            </pre>
                          )}
                        </Paper>
                      )}
                    {selectedRecord.status === "failed" && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        {selectedRecord.error}
                      </Alert>
                    )}
                  </Box>
                </>
              )}
            </DialogContent>

            <DialogActions
              sx={{ p: { xs: 1, sm: 2 }, gap: 1, flexWrap: "wrap" }}
            >
              {selectedRecord?.isBatch && selectedRecord.policies && (
                <Button
                  onClick={() => handleDownload(selectedRecord)}
                  variant="outlined"
                  color="success"
                  size={isMobile ? "small" : "medium"}
                  startIcon={<DownloadIcon />}
                >
                  Download All ({selectedRecord.policyCount})
                </Button>
              )}
              {getCurrentPolicyData() && (
                <Button
                  onClick={() =>
                    handleDownload(
                      selectedRecord!,
                      selectedRecord?.isBatch ? selectedPolicyIndex : undefined,
                    )
                  }
                  variant="contained"
                  color="success"
                  size={isMobile ? "small" : "medium"}
                  startIcon={<DownloadIcon />}
                  fullWidth={isMobile}
                >
                  {selectedRecord?.isBatch
                    ? `Download: ${selectedRecord.policies?.[selectedPolicyIndex]?.appnum || "Selected"}`
                    : "Download"}
                </Button>
              )}
              <Button
                onClick={() => setViewDialogOpen(false)}
                size={isMobile ? "small" : "medium"}
                fullWidth={isMobile}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            fullScreen={isMobile}
            maxWidth="xl"
            fullWidth
            open={dashboardOpen}
            onClose={() => setDashboardOpen(false)}
            PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
          >
            <DialogTitle
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                bgcolor: "white",
                borderBottom: "1px solid #E2E8F0",
                p: 2,
              }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Dashboard Analysis
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Comprehensive Insurance Risk Report
                </Typography>
              </Box>
              <IconButton onClick={() => setDashboardOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0, bgcolor: "#F8FAFC" }}>
              <LossRunDashboard
                augmentedPolicy={getDashboardPolicyData()}
                record={dashboardRecord}
                onClose={() => setDashboardOpen(false)}
              />
            </DialogContent>
          </Dialog>

          <Dialog
            open={reconciliationDialogOpen}
            onClose={() => setReconciliationDialogOpen(false)}
            maxWidth="lg"
            fullWidth
          >
            <DialogTitle sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
                  >
                    Reconciliation Reports
                  </Typography>
                  {selectedBatchId && (
                    <Chip
                      icon={<BatchIcon />}
                      label={`Batch: ${selectedBatchId.substring(0, 8)}...`}
                      size="small"
                      variant="outlined"
                      sx={{ fontFamily: "monospace" }}
                    />
                  )}
                </Box>
                <IconButton
                  onClick={() => setReconciliationDialogOpen(false)}
                  size="small"
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>

            <DialogContent sx={{ p: { xs: 1, sm: 2 } }}>
              {isLoadingReconciliation ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    py: 8,
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : reconciliationReports.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 8 }}>
                  <Typography variant="body2" color="text.secondary">
                    No reconciliation reports found for this batch.
                  </Typography>
                </Box>
              ) : (
                <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
                  <Table size="medium" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>App Number</TableCell>
                        <TableCell>Report File</TableCell>
                        <TableCell>Size</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reconciliationReports.map((report, index) => (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Chip
                              label={report.appnum}
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Tooltip title={report.filename}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: "0.85rem",
                                  maxWidth: 400,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {report.filename}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {(report.size / 1024).toFixed(1)} KB
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<DownloadIcon />}
                              onClick={() =>
                                handleDownloadReconciliationReport(
                                  report.filename,
                                )
                              }
                            >
                              Download
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </DialogContent>

            <DialogActions sx={{ p: { xs: 1, sm: 2 } }}>
              <Button onClick={() => setReconciliationDialogOpen(false)}>
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* Analysis Dialog */}
          <Dialog
            open={analysisDialogOpen}
            onClose={() => setAnalysisDialogOpen(false)}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { borderRadius: 2 } }}
          >
            <DialogTitle
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                bgcolor: "#f5f7fa",
                borderBottom: "2px solid #1a73e8",
                py: 2,
              }}
            >
              <MagicWandIcon sx={{ color: "#1a73e8", fontSize: 28 }} />
              <Typography
                variant="h6"
                component="span"
                sx={{ flexGrow: 1, fontWeight: 600, color: "#2c3e50" }}
              >
                Loss Run Analysis
              </Typography>
              <IconButton
                onClick={() => setAnalysisDialogOpen(false)}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <Box
              sx={{ display: "flex", flexDirection: "column", height: "600px" }}
            >
              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  p: 3,
                  bgcolor: "#ffffff",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                {conversationHistory.map((msg, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      justifyContent:
                        msg.role === "user" ? "flex-end" : "flex-start",
                      mb: 1,
                    }}
                  >
                    {msg.role === "assistant" && (
                      <Box
                        sx={{
                          maxWidth: "85%",
                          bgcolor: "#f0f2f5",
                          borderRadius: "12px",
                          p: 2,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "13px",
                            color: "#2c3e50",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {msg.content}
                        </Typography>
                      </Box>
                    )}
                    {msg.role === "user" && msg.content.length < 500 && (
                      <Box
                        sx={{
                          maxWidth: "70%",
                          bgcolor: "#1a73e8",
                          color: "#ffffff",
                          borderRadius: "12px",
                          p: 1.5,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#ffffff",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {msg.content}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                ))}
                {isAnalyzing && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      color: "#5f6368",
                    }}
                  >
                    <CircularProgress size={16} />
                    <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                      Thinking...
                    </Typography>
                  </Box>
                )}
                {!isAnalyzing && conversationHistory.length > 0 && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: "1px dashed #e0e0e0" }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#5f6368",
                        fontSize: "11px",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        mb: 1.5,
                        display: "block",
                      }}
                    >
                      Explore Provenance
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ gap: 1 }}
                    >
                      {getFollowUpSuggestions().map((s, i) => (
                        <Chip
                          key={i}
                          label={s.label}
                          onClick={() => setFollowUpQuestion(s.question)}
                          size="small"
                          variant="outlined"
                          sx={{
                            cursor: "pointer",
                            bgcolor: "#f0f7ff",
                            borderColor: "#1a73e8",
                            color: "#2c3e50",
                            "&:hover": { bgcolor: "#e3f2fd" },
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
                <div ref={(el) => el?.scrollIntoView({ behavior: "smooth" })} />
              </Box>

              <Box
                sx={{
                  bgcolor: "#f8f9fa",
                  p: 2,
                  borderTop: "1px solid #e0e0e0",
                  display: "flex",
                  gap: 1,
                }}
              >
                <TextField
                  fullWidth
                  multiline
                  maxRows={3}
                  value={followUpQuestion}
                  onChange={(e) => setFollowUpQuestion(e.target.value)}
                  placeholder="Ask a follow-up question..."
                  disabled={isAnalyzing}
                  onKeyPress={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleFollowUpQuestion();
                    }
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { bgcolor: "white" } }}
                />
                <IconButton
                  onClick={handleFollowUpQuestion}
                  disabled={isAnalyzing || !followUpQuestion.trim()}
                  sx={{
                    bgcolor: "#1a73e8",
                    color: "white",
                    "&:hover": { bgcolor: "#1557b0" },
                    "&:disabled": { bgcolor: "#e0e0e0" },
                  }}
                >
                  {isAnalyzing ? (
                    <CircularProgress size={20} sx={{ color: "white" }} />
                  ) : (
                    <SendIcon />
                  )}
                </IconButton>
              </Box>
            </Box>

            <DialogActions
              sx={{
                bgcolor: "#f8f9fa",
                px: 3,
                py: 1.5,
                borderTop: "1px solid #e0e0e0",
              }}
            >
              <Box sx={{ mr: "auto" }}>
                {analysisSelectedRecord && (
                  <Typography
                    variant="caption"
                    sx={{ fontSize: "11px", color: "#5f6368" }}
                  >
                    Record: {analysisSelectedRecord.pdfFileName}
                  </Typography>
                )}
              </Box>
              <Button
                onClick={() => {
                  setAnalysisDialogOpen(false);
                  setFollowUpQuestion("");
                }}
                variant="outlined"
                size="small"
                disabled={isAnalyzing}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>
    </ThemeProvider >
  );
};

export default App;
